import z from 'zod'

/** Playback subset for scheduling `playlist_embedding` rows (e.g. ad orders). */
export const playlistEmbedPlaybackSchema = z.object({
  /** null = all frames from the child playlist. */
  frameCount: z.number().int().min(1).nullable(),
  selectionMode: z.enum(['random', 'rotating'])
})

export type TPlaylistEmbedPlayback = z.infer<typeof playlistEmbedPlaybackSchema>

/**
 * Coerce “play all frames inline” to `null` for persistence (matches group master / scheduling semantics).
 */
export function normalizePlaylistEmbedPlaybackForPersistence(
  v: TPlaylistEmbedPlayback | null | undefined
): TPlaylistEmbedPlayback | null {
  if (v == null) {
    return null
  }
  if (v.frameCount === null && v.selectionMode === 'rotating') {
    return null
  }
  return v
}

/**
 * Parse embed playback from SQLite JSON / JSON strings / snake_case payloads.
 * Invalid shapes return `null` so callers fall back to inline child frames (legacy “all frames”).
 */
export function coercePlaylistEmbedPlaybackFromStorage(raw: unknown): TPlaylistEmbedPlayback | null {
  let v: unknown = raw
  if (typeof raw === 'string') {
    const t = raw.trim()
    if (t === '' || t === 'null') {
      return null
    }
    try {
      v = JSON.parse(t) as unknown
    }
    catch {
      return null
    }
  }
  if (v == null || typeof v !== 'object' || Array.isArray(v)) {
    return null
  }
  const o = v as Record<string, unknown>
  const fc = o.frameCount ?? o.frame_count
  const sm = o.selectionMode ?? o.selection_mode
  let frameCount: number | null = null
  if (fc !== null && fc !== undefined) {
    if (typeof fc === 'number' && Number.isInteger(fc) && fc >= 1) {
      frameCount = fc
    }
    else if (typeof fc === 'string' && /^\d+$/.test(fc)) {
      const n = Number.parseInt(fc, 10)
      if (Number.isFinite(n) && n >= 1) {
        frameCount = n
      }
      else {
        return null
      }
    }
    else {
      return null
    }
  }
  const selectionMode = sm === 'random' || sm === 'rotating' ? sm : 'rotating'
  const parsed = playlistEmbedPlaybackSchema.safeParse({ frameCount, selectionMode })
  if (!parsed.success) {
    return null
  }
  return normalizePlaylistEmbedPlaybackForPersistence(parsed.data)
}
